import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
} from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LanguageIcon from "@mui/icons-material/Language";
import BarChartIcon from "@mui/icons-material/BarChart";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForward";
import LaunchIcon from "@mui/icons-material/Launch";
import InfoBox from "../utils/InfoBox";
import { getImageUrl } from "../../../utils/helpers";

const KthPatentAnalysis = ({ baseConfig, sectionStyles }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    secColor,
    quadColor,
    quadHoverColor,
    linkColor,
  } = baseConfig.siteBranding;

  return (
    <Card sx={{ ...sectionStyles.card, mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
        }}
      >
        <AnalyticsIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h6" fontWeight="bold">
          Patent Analysis & Portfolio Counseling
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              Understanding the value, strength, and competitive positioning of
              your patent portfolio is essential for maximizing your
              intellectual property investment. Our patent analysis services
              leverage both technical expertise and advanced AI tools to deliver
              comprehensive insights that guide your strategic decision-making.
            </Typography>

            <Typography variant="body1" paragraph>
              Whether you're evaluating potential infringement, assessing
              freedom to operate, or seeking to optimize your patent strategy,
              our attorney-in-the-loop analysis approach combines human
              expertise with technological efficiency to save your budget and
              time while maintaining quality.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                color={tertColor}
              >
                Our Patent Analysis & Portfolio Services:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Comprehensive prior art searches"
                    secondary="Discover existing technologies relevant to your innovations"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Infringement analysis"
                    secondary="Determine if products potentially infringe your patents or if you may be infringing others'"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Patent landscape mapping"
                    secondary="Visualize the competitive patent environment in your technology space"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Freedom to operate (FTO) assessments"
                    secondary="Evaluate risks before launching new products or entering new markets"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Patent portfolio optimization"
                    secondary="Identify strengths, weaknesses, and gaps in your patent coverage"
                  />
                </ListItem>
              </List>
            </Box>

            <Box
              sx={{
                p: 2,
                mt: 2,
                backgroundColor: "#e3f2fd",
                borderRadius: 2,
                border: "1px solid #90caf9",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                color="#0d47a1"
              >
                AI-Enhanced Patent Analysis
              </Typography>
              <Typography variant="body2">
                We combine attorney expertise with advanced AI tools to
                efficiently analyze patent documents, identify relevant prior
                art, and generate insights about your portfolio. This hybrid
                approach delivers more thorough results than either humans or AI
                alone, while significantly reducing costs and accelerating
                delivery times.
              </Typography>
              <Button
                component={RouterLink}
                to="/patent-ai-resources"
                sx={{ mt: 1, color: "#0d47a1" }}
                endIcon={<LaunchIcon />}
              >
                Learn More
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <img
                src={getImageUrl("patent-analysis.png")}
                alt="Patent Analysis & Portfolio Management"
                style={{
                  width: "100%",
                  maxWidth: "450px",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              />
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography variant="h6" gutterBottom color={tertColor}>
                Strategic Patent Portfolio Management
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <SearchIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Competitive Intelligence
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Track competitor patent activity to anticipate market
                        moves and identify technology trends before they emerge.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <AssessmentIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Portfolio Valuation
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Determine the financial value of your patent assets for
                        transactions, fundraising, or financial reporting.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <LanguageIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Global Protection Strategy
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Develop international filing strategies that align with
                        your business markets and manufacturing locations.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      backgroundColor: "white",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <BarChartIcon sx={{ color: tertColor, mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          ROI Optimization
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Maximize return on investment by focusing resources on
                        patents that best support your business objectives.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* AI-Powered Patent Analysis Section */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: tertColor,
              borderBottom: `1px solid ${tertColor}33`,
              pb: 1,
            }}
          >
            AI-Powered Patent Analysis
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <img
                  src={getImageUrl("kth-ai-in-patent-analysis.png")}
                  alt="AI-Powered Patent Analysis"
                  style={{
                    width: "100%",
                    maxWidth: "300px",
                    height: "auto",
                    borderRadius: "8px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                Our AI-enhanced patent analysis services combine cutting-edge
                artificial intelligence tools with attorney oversight to deliver
                faster, more cost-effective, and more comprehensive results than
                traditional methods alone. This attorney-in-the-loop approach
                ensures you get the benefits of AI efficiency while maintaining
                the quality and judgment of human legal expertise.
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                color={tertColor}
              >
                Benefits of Our AI-Powered Approach:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <SmartToyIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="50-70% faster turnaround times" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <SmartToyIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Significantly reduced costs" />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <SmartToyIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="More comprehensive search coverage" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <SmartToyIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Advanced semantic understanding" />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              <Box
                sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}
              >
                <Button
                  component={RouterLink}
                  to="/ai#ai-patent-analysis"
                  variant="contained"
                  sx={{
                    backgroundColor: tertColor,
                    color: "white",
                    "&:hover": {
                      color: "white",
                      backgroundColor: tertHoverColor,
                    },
                  }}
                  endIcon={<ArrowForwardIosIcon />}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Case Study/Client Testimonial Section */}
        {/* <InfoBox
          title="Success Story: Portfolio Analysis for Tech Startup"
          content={
            <Typography variant="body2">
              We helped a technology startup identify key patents in their
              portfolio that were most valuable for securing venture funding.
              Using our AI-powered analysis tools, we mapped their innovations
              against competitor activity and market trends, highlighting unique
              technical advantages. The resulting strategic patent report was
              instrumental in their successful Series A funding round, with
              investors specifically citing the strength of their IP position as
              a decisive factor.
            </Typography>
          }
          type="success"
        /> */}
      </CardContent>
    </Card>
  );
};

export default KthPatentAnalysis;
